import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./styles/globals.css";
import "./lib/sentry";

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Root element not found");
const root = createRoot(rootEl);

// `/changelog` is public — see routes/changelog.tsx. It mounts on its own,
// ahead of every provider, because none of them are its concern:
//
//   * Clerk. The page has no signed-in user and asks for none. Rendering it
//     under ClerkProvider would make a public page fail whenever Clerk fails
//     to initialise, and would require VITE_CLERK_PUBLISHABLE_KEY to be valid
//     for a page that never authenticates anyone.
//   * Convex. Nothing here is queried — the entries are a compiled-in array —
//     so there is no reason to open a websocket.
//   * The service worker. Precaching the whole ops app for a visitor who came
//     to read one page is bytes nobody asked for.
//
// Both branches are dynamic imports so neither pays for the other's bundle:
// a reader of /changelog never downloads the Clerk or Convex vendor chunks.
const path = window.location.pathname.replace(/\/+$/, "");
if (path === "/changelog") {
  const { PublicChangelog } = await import("./routes/changelog");
  root.render(
    <StrictMode>
      <PublicChangelog />
    </StrictMode>,
  );
} else {
  const [
    { ClerkProvider, useAuth },
    { ConvexProviderWithClerk },
    { convex },
    { default: App },
  ] = await Promise.all([
    import("@clerk/react"),
    import("convex/react-clerk"),
    import("./lib/convex"),
    import("./App"),
    // Side-effect only: registers the service worker for the app itself.
    import("./lib/pwa"),
  ]);

  const publishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as
    | string
    | undefined;
  if (!publishableKey) throw new Error("VITE_CLERK_PUBLISHABLE_KEY is required");

  root.render(
    <StrictMode>
      <ClerkProvider publishableKey={publishableKey} afterSignOutUrl="/">
        <ConvexProviderWithClerk client={convex} useAuth={useAuth}>
          <App />
        </ConvexProviderWithClerk>
      </ClerkProvider>
    </StrictMode>,
  );
}
